import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
import { recentPurchases } from '../data/recentPurchases';
const FIRST_DELAY = 6000;
const VISIBLE_MS = 5500;
const GAP_MS = 12000;

/**
 * Low-frequency "someone just bought this" notification, bottom-left so it
 * never covers the CTA. Stays hidden until real orders are added to
 * data/recentPurchases.ts.
 */
export function PurchaseNotifications() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hasData = recentPurchases.length > 0;
  useEffect(() => {
    if (dismissed || !hasData) return;
    let timeout: ReturnType<typeof setTimeout>;
    const show = () => {
      setVisible(true);
      timeout = setTimeout(hide, VISIBLE_MS);
    };
    const hide = () => {
      setVisible(false);
      timeout = setTimeout(() => {
        setIndex(i => (i + 1) % recentPurchases.length);
        show();
      }, GAP_MS);
    };
    timeout = setTimeout(show, FIRST_DELAY);
    return () => clearTimeout(timeout);
  }, [dismissed, hasData]);
  if (dismissed || !hasData) return null;
  const purchase = recentPurchases[index];
  const minutes = 2 + index * 7 % 26;
  return <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)] sm:bottom-6 sm:left-6" data-id="element-274" data-mp="etkov03fxnxv">
      <AnimatePresence data-id="element-275" data-mp="17l4fh52da49w">
        {visible && <motion.div key={index} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 14,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto flex w-[320px] max-w-full items-start gap-3 rounded-xl border border-line bg-white p-3.5 shadow-card" data-id="element-276" data-mp="17i9wlhz04ril">
            <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-green" data-id="element-277" data-mp="3kopvtk0lvids">
              <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-278" data-mp="3pnjahfrmpffj" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-279" data-mp="3auzaup3otq2u">
              <p className="text-[13px] leading-snug text-ink" data-id="element-280" data-mp="3fqogna6aw37l">
                <span className="font-bold" data-id="element-281" data-mp="320b56xu7a6di">{purchase.name}</span> in {purchase.city},{' '}
                {purchase.state} just got Badminton Class Ready
              </p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-ink-muted" data-id="element-282" data-mp="36t4cnth1zsta">
                {minutes} minutes ago
              </p>
            </div>
            <button type="button" onClick={() => setDismissed(true)} aria-label="Dismiss purchase notifications" className="-mr-1 -mt-1 flex h-7 w-7 flex-none items-center justify-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-court" data-id="element-283" data-mp="2x1zyux346e01">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-284" data-mp="1zh87m8oxjps5" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}